import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { emailSchema } from '@tailoring-crm/shared';
import { useApiClient } from '../../lib/api/api-context.js';
import { loginWithPassword } from '../../lib/auth/auth-api.js';
import type { LoginResult } from '../../lib/auth/auth-contract.js';
import { authErrorMessage } from '../../lib/auth/auth-errors.js';
import {
  forgetDevice,
  rememberDevice,
  type LoginMethod,
} from '../../lib/auth/remembered-device.js';
import { Button } from '@tailoring-crm/ui';
import { FormError, PasswordField, EmailField } from './auth-fields.js';

/**
 * The credential half of signing in, on its own so the login screen and the
 * session-expired dialog are the same code and cannot drift apart.
 *
 * Email and password, and nothing else. The PIN keypad this form used to offer as a second
 * method was removed on 2026-09-03 — one way in is simpler to explain at the counter than two.
 * `initialMethod` is still accepted so a remembered device from before then does not break the
 * screen; it is ignored.
 */
export interface CredentialFormProps {
  /** Prefilled, and locked, when we already know who is signing back in. */
  email?: string;
  lockEmail?: boolean;
  initialMethod?: LoginMethod;
  submitLabel: string;
  onAuthenticated: (result: LoginResult, context: { email: string; method: LoginMethod }) => void;
  /** Rendered under the submit button — "forgot password?", a signup link. */
  children?: ReactElement | null;
}

export function CredentialForm({
  email: initialEmail = '',
  lockEmail = false,
  initialMethod = 'password',
  submitLabel,
  onAuthenticated,
  children,
}: CredentialFormProps): ReactElement {
  const { t } = useTranslation();
  const client = useApiClient();

  const method: LoginMethod = 'password';
  void initialMethod;
  const [email, setEmail] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | undefined>(undefined);

  const submit = (): void => {
    const parsedEmail = emailSchema.safeParse(email);
    if (!parsedEmail.success) {
      setEmailError(parsedEmail.error.issues[0]?.message);
      return;
    }
    setEmailError(undefined);

    const secret = password;
    if (secret.length === 0) {
      setError(t('auth.enterPassword'));
      return;
    }

    setBusy(true);
    setError(null);
    void (async () => {
      try {
        const result = await loginWithPassword(client, {
          email: parsedEmail.data,
          password: secret,
        });

        if (remember) rememberDevice({ email: parsedEmail.data, method });
        else forgetDevice();

        onAuthenticated(result, { email: parsedEmail.data, method });
      } catch (caught) {
        setError(authErrorMessage(caught, t));
        // Clear only the secret. Retyping a whole address because a password was wrong is the
        // kind of small cruelty that makes people stop using software.
        setPassword('');
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <form
      className="flex flex-col gap-5"
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
      noValidate
    >
      <FormError message={error} />

      <EmailField
        label={t('auth.emailAddress')}
        error={emailError}
        value={email}
        readOnly={lockEmail}
        autoFocus={!lockEmail && email === ''}
        onChange={(event) => setEmail(event.target.value)}
      />

      <PasswordField
        label={t('auth.password')}
        autoComplete="current-password"
        value={password}
        onChange={(event) => setPassword(event.target.value)}
      />

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          className="size-4"
          checked={remember}
          onChange={(event) => setRemember(event.target.checked)}
        />
        {t('auth.rememberDevice')}
      </label>

      <Button type="submit" block disabled={busy}>
        {submitLabel}
      </Button>

      {children}
    </form>
  );
}
